import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TopNavigation from '../../components/feature/TopNavigation';
import BottomNavigation from '../../components/feature/BottomNavigation';
import Card from '../../components/base/Card';
import Button from '../../components/base/Button';

export default function Payments() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'pending' | 'paid' | 'all'>('pending');

  const paymentSummary = {
    totalEarnings: '12,500',
    pendingAmount: '2,500',
    paidThisMonth: '4,200',
    nextPayment: '2024-02-01'
  };

  const payments = {
    pending: [
      {
        id: 1,
        project: 'معرض الرياض الدولي للتقنية',
        amount: '1,200',
        days: 8,
        rate: '150',
        period: '15-18 يناير 2024',
        status: 'قيد المراجعة',
        dueDate: '2024-02-01'
      },
      {
        id: 2,
        project: 'مهرجان الرياض للتسوق',
        amount: '1,300',
        days: 6.5,
        rate: '200',
        period: '20-25 يناير 2024',
        status: 'معتمد للدفع',
        dueDate: '2024-02-01'
      }
    ],
    paid: [
      {
        id: 3,
        project: 'مؤتمر الذكاء الاصطناعي',
        amount: '540',
        days: 3,
        rate: '180',
        period: '5-7 يناير 2024',
        status: 'مدفوع',
        paidDate: '2024-01-15',
        paymentMethod: 'تحويل بنكي'
      },
      {
        id: 4,
        project: 'معرض الكتاب الدولي',
        amount: '3,660',
        days: 10,
        rate: '130',
        period: '1-10 ديسمبر 2023',
        status: 'مدفوع',
        paidDate: '2024-01-01',
        paymentMethod: 'تحويل بنكي'
      }
    ],
    all: []
  };

  // Combine all payments for 'all' tab
  payments.all = [...payments.pending, ...payments.paid];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'قيد المراجعة': return 'text-[#FBC02D] bg-[#FBC02D]/10';
      case 'معتمد للدفع': return 'text-[#4CAF50] bg-[#4CAF50]/10';
      case 'مدفوع': return 'text-[#607D8B] bg-[#607D8B]/10';
      default: return 'text-[#757575] bg-[#F5F5F5]';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ar-SA', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <div className="min-h-screen bg-[#FDFDFD]">
      <TopNavigation 
        title="المستحقات المالية"
        leftAction={
          <button 
            onClick={() => navigate('/dashboard')}
            className="w-8 h-8 flex items-center justify-center"
          >
            <i className="ri-arrow-right-line text-lg"></i>
          </button>
        }
        rightAction={
          <button className="w-8 h-8 flex items-center justify-center">
            <i className="ri-download-line text-lg"></i>
          </button>
        }
      />

      <div className="pt-14 pb-20 px-4">
        {/* Payment Summary */}
        <Card className="mb-6 mt-4 bg-gradient-to-r from-[#1565C0] to-[#1976D2] text-white">
          <div className="text-center py-6">
            <h2 className="text-2xl font-bold mb-2">{paymentSummary.totalEarnings} ريال</h2>
            <p className="text-white/90 mb-4">إجمالي الأرباح</p>
            <div className="grid grid-cols-2 gap-4 text-center">
              <div>
                <p className="text-lg font-semibold">{paymentSummary.pendingAmount} ريال</p>
                <p className="text-white/80 text-sm">قيد الانتظار</p>
              </div>
              <div>
                <p className="text-lg font-semibold">{paymentSummary.paidThisMonth} ريال</p>
                <p className="text-white/80 text-sm">هذا الشهر</p>
              </div>
            </div>
          </div>
        </Card>

        {/* Next Payment Info */}
        <Card className="mb-6 border-r-4 border-[#4CAF50]">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-[#4CAF50]/10 rounded-full flex items-center justify-center ml-4">
              <i className="ri-calendar-check-line text-[#4CAF50] text-xl"></i>
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-[#333333]">الدفعة القادمة</h3>
              <p className="text-[#757575] text-sm">
                {formatDate(paymentSummary.nextPayment)} - {paymentSummary.pendingAmount} ريال
              </p>
            </div>
          </div>
        </Card>

        {/* Tabs */}
        <div className="mb-6">
          <div className="flex bg-[#F5F5F5] rounded-lg p-1">
            {[
              { key: 'pending', label: 'قيد الانتظار' },
              { key: 'paid', label: 'المدفوعة' },
              { key: 'all', label: 'الكل' }
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key as any)}
                className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                  activeTab === tab.key
                    ? 'bg-white text-[#1565C0] shadow-sm'
                    : 'text-[#607D8B]'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Payments List */}
        <div className="space-y-4">
          {payments[activeTab].map((payment) => (
            <Card key={payment.id} className="hover:shadow-md transition-shadow">
              <div className="space-y-4">
                {/* Payment Header */}
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-[#333333] text-lg mb-1">{payment.project}</h3>
                    <p className="text-[#607D8B] text-sm">{payment.period}</p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(payment.status)}`}>
                    {payment.status}
                  </span>
                </div>

                {/* Payment Details */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="flex items-center">
                    <i className="ri-money-dollar-circle-line text-[#4CAF50] ml-2"></i>
                    <div>
                      <p className="text-[#757575]">المبلغ</p>
                      <p className="font-semibold text-[#333333]">{payment.amount} ريال</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-calendar-line text-[#1565C0] ml-2"></i>
                    <div>
                      <p className="text-[#757575]">عدد الأيام</p>
                      <p className="font-semibold text-[#333333]">{payment.days} يوم</p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center">
                    <i className="ri-price-tag-line text-[#607D8B] ml-2"></i>
                    <span className="text-[#757575]">المعدل اليومي: </span>
                    <span className="font-semibold text-[#333333]">{payment.rate} ريال</span>
                  </div>
                </div>

                {/* Payment Date Info */}
                {payment.status === 'مدفوع' && 'paidDate' in payment && (
                  <div className="pt-3 border-t border-gray-100">
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center">
                        <i className="ri-check-circle-line text-[#4CAF50] ml-2"></i>
                        <span className="text-[#757575]">تاريخ الدفع: </span>
                        <span className="font-medium text-[#333333]">{formatDate(payment.paidDate)}</span>
                      </div>
                      <span className="text-[#607D8B]">{payment.paymentMethod}</span>
                    </div>
                  </div>
                )}

                {payment.status !== 'مدفوع' && 'dueDate' in payment && (
                  <div className="pt-3 border-t border-gray-100">
                    <div className="flex items-center text-sm">
                      <i className="ri-time-line text-[#FBC02D] ml-2"></i>
                      <span className="text-[#757575]">تاريخ الدفع المتوقع: </span>
                      <span className="font-medium text-[#333333]">{formatDate(payment.dueDate)}</span>
                    </div>
                  </div>
                )}

                {/* Actions */}
                {payment.status === 'مدفوع' && (
                  <div className="flex gap-3">
                    <Button variant="outline" size="sm" className="flex-1">
                      <i className="ri-download-line ml-2"></i>
                      تحميل الإيصال
                    </Button>
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>

        {payments[activeTab].length === 0 && (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-[#F5F5F5] rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-money-dollar-circle-line text-[#607D8B] text-2xl"></i>
            </div>
            <h3 className="text-lg font-semibold text-[#333333] mb-2">لا توجد مدفوعات</h3>
            <p className="text-[#757575] mb-4">ابدأ بالعمل في المشاريع لتحصل على أرباحك</p>
            <Button variant="primary" onClick={() => navigate('/jobs')}>
              استعراض الوظائف
            </Button>
          </div>
        )}

        {/* Bank Account Info */}
        <Card className="mt-6">
          <h3 className="text-lg font-semibold text-[#333333] mb-4">معلومات الحساب البنكي</h3>
          <div className="space-y-3 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-[#757575]">البنك</span>
              <span className="font-medium text-[#333333]">البنك الأهلي السعودي</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[#757575]">رقم الحساب</span>
              <span className="font-medium text-[#333333]">**** **** **** 1234</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[#757575]">اسم صاحب الحساب</span>
              <span className="font-medium text-[#333333]">أحمد محمد العلي</span>
            </div>
          </div>
          <Button variant="outline" size="sm" className="w-full mt-4">
            <i className="ri-edit-line ml-2"></i>
            تعديل معلومات الحساب
          </Button>
        </Card>
      </div>

      <BottomNavigation />
    </div>
  );
}